/* Connecting an MCP connection to the BeX Platform MCP.
 *
 * The BeX MCP accepts only short-lived OAuth tokens: a bearer pasted into a
 * connection works for at most an hour, then every call 401s. What a headless
 * caller needs instead is a service client (OAuth client_credentials), which
 * Project-O trades for fresh tokens itself.
 *
 *   <BexMcpCredentials>   mints one and hands back the filled oauth2 fields
 *   <BexMcpBearerWarning> says so before a builder pastes a token
 */
import {
    useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    mcpServiceClientService
} from 'shared/api/services/BEX/mcpServiceClient.service';

// Served from the same host as this app, at /api/mcp.
export const isBexMcpUrl = (url) => {
    try {
        const u = new URL(url);
        return u.host === window.location.host && /^\/api\/mcp\/?$/.test(u.pathname);
    } catch { return false; }
};

// axios error -> the server's words. Express routes answer {error}, FastAPI
// {detail}; a plain object must never render as "[object Object]".
export const errorText = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.error || data?.detail || data?.message || err?.message || String(err);
};

// The connection draft with a minted service client applied: the BeX MCP
// URL, auth type oauth2, client id and token URL. (The secret is NOT part of
// the draft -- it belongs in the form's write-only secret field.)
export const withBexCredentials = (draft, created) => ({
    ...draft,
    server_url: created.mcp_url,
    auth: {
        ...draft.auth,
        type: 'oauth2',
        oauth2_client_id: created.client_id,
        oauth2_token_url: created.token_url,
    },
});

export const BexMcpBearerWarning = ({
    serverUrl
}) => (isBexMcpUrl(serverUrl) ? (
    <p className="text-xs text-amber-700 dark:text-amber-400">
        This looks like the BeX Platform MCP, which accepts only OAuth tokens that expire within
        an hour — a pasted token will stop working. Choose auth type <strong>oauth2</strong> and
        use “Generate BeX MCP credentials”.
    </p>
) : null);

/**
 * Mints a service client and applies it to the connection draft.
 *
 * @param {function} onSecret  (secret) => void. The secret is handed over
 *   ONCE; the form puts it in its write-only secret field so the normal save
 *   path stores it in Vault. It is never part of the draft.
 */
const BexMcpCredentials = ({
    orgId, subOrgId, draft, onDraftChange, onSecret, saveLabel, disabled,
}) => {
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(null);
    const [error, setError] = useState('');

    const generate = async () => {
        setError('');
        setGenerating(true);
        try {
            const created = await mcpServiceClientService.create({
                organizationId: orgId,
                subOrganizationId: subOrgId,
                name: `MCP connection: ${draft.connection_name?.trim() || 'unnamed'}`,
            });
            onDraftChange(withBexCredentials(draft, created));
            onSecret(created.client_secret);
            setGenerated(created);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
                Connecting to the <strong>BeX Platform MCP</strong>? Generate a service client for
                this sub-organization — it fills the fields below, and Project-O fetches fresh
                tokens itself.
            </p>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generate}
                disabled={generating || disabled}
            >
                {generating ? 'Generating…' : 'Generate BeX MCP credentials'}
            </Button>
            {error && <p className="text-xs text-destructive">{error}</p>}
            {generated && (
                <p className="text-xs text-muted-foreground">
                    Created <code>{generated.client_id}</code> with scopes{' '}
                    <code>{(generated.scopes || []).join(' ')}</code>. The secret is stored in Vault
                    when you <strong>{saveLabel}</strong> and cannot be shown again.
                </p>
            )}
        </div>
    );
};

export default BexMcpCredentials;
